import {Dispatch, PropsWithChildren, SetStateAction, useState} from "react";
import {Button, Result} from "antd";
import {createContext} from "../../create-context";
import {FullPageLoader} from "../../../components/full-page-loader";
import {PaginationMeta} from "../../../redux/service/i-service";
import {GetCustomerPastCallsApiResponse} from "../../../react-query-hooks/session/past-calls/types";
import {useGetCustomerPastCalls} from "../../../react-query-hooks/session/past-calls";

type GetCustomerPastCallsState = {
  customerPastCallsData: GetCustomerPastCallsApiResponse;
  customerPastCallsApiMetaResponse: PaginationMeta;
  isFetchingCustomerPastCalls: boolean;
  isPreviousCustomerPastCallsData: boolean;
  isLoadingCustomerPastCalls: boolean;
  refetchCustomerPastCalls: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
};

type GetCustomerPastCallsContextState = {
  state: GetCustomerPastCallsState;
};

const [useContext, ContextProvider] = createContext<GetCustomerPastCallsContextState>();

export const useGetCustomerPastCallsContext = useContext;

export const GetCustomerPastCallsProvider = ({children}: PropsWithChildren) => {
  const [page, setPage] = useState(1);
  const getCustomerPastCallsQuery = useGetCustomerPastCalls(page);

  if (getCustomerPastCallsQuery.isLoading) {
    return <FullPageLoader />;
  }

  if (
    getCustomerPastCallsQuery.isError &&
    getCustomerPastCallsQuery.error.message !==
      "Sorry, you currently do not have any past/completed bookings!"
  ) {
    return (
      <Result
        status="500"
        title="Sorry, something went wrong."
        subTitle="Error fetching upcoming calls"
        extra={
          <Button
            type="primary"
            size="small"
            onClick={() => getCustomerPastCallsQuery.refetch()}
          >
            Try again
          </Button>
        }
      />
    );
  }

  function refetchCustomerPastCalls() {
    getCustomerPastCallsQuery.refetch();
  }

  const state: GetCustomerPastCallsState = {
    customerPastCallsData: getCustomerPastCallsQuery.isSuccess
      ? getCustomerPastCallsQuery.data.data
      : {
          bookings: [],
        },
    customerPastCallsApiMetaResponse: getCustomerPastCallsQuery.isSuccess
      ? getCustomerPastCallsQuery.data.meta
      : {
          current_page: 1,
          next_page: 0,
          per_page: 5,
          previous_page: 0,
          total_pages: 0,
        },
    isFetchingCustomerPastCalls: getCustomerPastCallsQuery.isFetching,
    isLoadingCustomerPastCalls: getCustomerPastCallsQuery.isLoading,
    isPreviousCustomerPastCallsData: getCustomerPastCallsQuery.isPreviousData,
    refetchCustomerPastCalls,
    page,
    setPage,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
